/**
 * Primary Ledger Snapshots - Metric Aggregation Utilities
 * 
 * Functions for calculating aggregated metrics from Primary Ledger data
 * with comparison to previous periods and proper handling of edge cases.
 */

import {
  MetricValue,
  OverallMetrics,
  MarginMetrics,
  PPCMetrics,
  PPCMetricWithAverage,
  ChangeDirection
} from '@/types/primaryLedgerSnapshots';
import {
  SalesProfitHistory,
  PPCResults,
  MarginHistory
} from '@/types/primaryLedger';

/**
 * Calculate a metric value with comparison to previous period
 * @param current Current period value
 * @param previous Previous period value (optional)
 * @returns MetricValue with change calculations
 */
export function calculateMetricValue(
  current: number,
  previous?: number
): MetricValue {
  if (previous === undefined || previous === null) {
    return {
      current,
      changeDirection: 'neutral'
    };
  }

  const change = current - previous;
  const changePercent = previous === 0 
    ? (current === 0 ? 0 : 100) 
    : (change / previous) * 100;

  let changeDirection: ChangeDirection;
  if (Math.abs(changePercent) < 0.01) {
    changeDirection = 'neutral';
  } else {
    changeDirection = change >= 0 ? 'up' : 'down';
  }

  return {
    current,
    previous,
    change,
    changePercent,
    changeDirection
  };
}

/**
 * Calculate a PPC metric with both cumulative and daily average
 * @param currentTotal Current period total
 * @param currentDays Number of days in current period
 * @param previousTotal Previous period total (optional)
 * @param previousDays Number of days in previous period (optional)
 * @returns PPCMetricWithAverage with cumulative and daily average
 */
function calculatePPCMetricWithAverage(
  currentTotal: number,
  currentDays: number,
  previousTotal?: number,
  previousDays?: number
): PPCMetricWithAverage {
  const currentAvg = currentDays > 0 ? currentTotal / currentDays : 0;
  const previousAvg = previousTotal !== undefined && previousDays && previousDays > 0
    ? previousTotal / previousDays
    : undefined;

  return {
    cumulative: calculateMetricValue(currentTotal, previousTotal),
    dailyAverage: calculateMetricValue(currentAvg, previousAvg)
  };
}

/**
 * Calculate overall sales and profit metrics from Sales/Profit History data
 * @param currentData Array of SalesProfitHistory for current period
 * @param previousData Array of SalesProfitHistory for previous period (optional)
 * @returns OverallMetrics with aggregated values
 */
export function calculateOverallMetrics(
  currentData: SalesProfitHistory[],
  previousData?: SalesProfitHistory[]
): OverallMetrics | null {
  if (!currentData || currentData.length === 0) {
    return null;
  }

  const currentRevenue = currentData.reduce((sum, d) => sum + (d.revenue || 0), 0);
  const currentUnits = currentData.reduce((sum, d) => sum + (d.units || 0), 0);
  const currentGrossProfit = currentData.reduce((sum, d) => sum + (d.grossProfit || 0), 0);
  const currentMarginPercent = currentRevenue > 0 
    ? (currentGrossProfit / currentRevenue) * 100 
    : 0;

  let previousRevenue: number | undefined;
  let previousUnits: number | undefined;
  let previousGrossProfit: number | undefined;
  let previousMarginPercent: number | undefined;

  if (previousData && previousData.length > 0) {
    previousRevenue = previousData.reduce((sum, d) => sum + (d.revenue || 0), 0);
    previousUnits = previousData.reduce((sum, d) => sum + (d.units || 0), 0);
    previousGrossProfit = previousData.reduce((sum, d) => sum + (d.grossProfit || 0), 0);
    previousMarginPercent = previousRevenue > 0 
      ? (previousGrossProfit / previousRevenue) * 100 
      : 0;
  }

  return {
    revenue: calculateMetricValue(currentRevenue, previousRevenue),
    units: calculateMetricValue(currentUnits, previousUnits),
    grossProfit: calculateMetricValue(currentGrossProfit, previousGrossProfit),
    profitMarginPercent: calculateMetricValue(currentMarginPercent, previousMarginPercent)
  };
}

/**
 * Calculate margin metrics from Margin History data (uses most recent values)
 * @param currentData Array of MarginHistory for current period
 * @param previousData Array of MarginHistory for previous period (optional)
 * @returns MarginMetrics with most recent values
 */
export function calculateMarginMetrics(
  currentData: MarginHistory[],
  previousData?: MarginHistory[]
): MarginMetrics | null {
  if (!currentData || currentData.length === 0) {
    return null;
  }

  const currentLatest = currentData[currentData.length - 1];
  const previousLatest = previousData && previousData.length > 0 
    ? previousData[previousData.length - 1] 
    : undefined;

  return {
    price: calculateMetricValue(
      currentLatest.price || 0,
      previousLatest?.price
    ),
    cogs: calculateMetricValue(
      currentLatest.cogs || 0,
      previousLatest?.cogs
    ),
    fbaFee: calculateMetricValue(
      currentLatest.fbaFee || 0,
      previousLatest?.fbaFee
    ),
    referralFeePercent: calculateMetricValue(
      currentLatest.referralFeePercent || 0,
      previousLatest?.referralFeePercent
    ),
    finalGrossProfit: calculateMetricValue(
      currentLatest.finalGrossProfitDollar || 0,
      previousLatest?.finalGrossProfitDollar
    ),
    finalTargetAcos: calculateMetricValue(
      currentLatest.finalTargetAcos || 0,
      previousLatest?.finalTargetAcos
    )
  };
}

/**
 * Calculate PPC metrics from PPC Results data with proper aggregation
 * @param currentData Array of PPCResults for current period
 * @param previousData Array of PPCResults for previous period (optional)
 * @returns PPCMetrics with cumulative totals, daily averages, and weighted averages
 */
export function calculatePPCMetrics(
  currentData: PPCResults[],
  previousData?: PPCResults[]
): PPCMetrics | null {
  if (!currentData || currentData.length === 0) {
    return null;
  }

  const currentDays = currentData.length;
  const previousDays = previousData?.length || 0;

  const currentImpressions = currentData.reduce((sum, d) => sum + (d.impressions || 0), 0);
  const currentClicks = currentData.reduce((sum, d) => sum + (d.clicks || 0), 0);
  const currentSpend = currentData.reduce((sum, d) => sum + (d.spend || 0), 0);
  const currentSales = currentData.reduce((sum, d) => sum + (d.sales || 0), 0);
  const currentOrders = currentData.reduce((sum, d) => sum + (d.orders || 0), 0);
  const currentUnits = currentData.reduce((sum, d) => sum + (d.units || 0), 0);

  const currentCTR = currentImpressions > 0 ? (currentClicks / currentImpressions) * 100 : 0;
  const currentCPC = currentClicks > 0 ? currentSpend / currentClicks : 0;
  const currentACoS = currentSales > 0 ? (currentSpend / currentSales) * 100 : 0;
  const currentROAS = currentSpend > 0 ? currentSales / currentSpend : 0;
  const currentCR = currentClicks > 0 ? (currentOrders / currentClicks) * 100 : 0;

  let previousImpressions: number | undefined;
  let previousClicks: number | undefined;
  let previousSpend: number | undefined;
  let previousSales: number | undefined;
  let previousOrders: number | undefined;
  let previousUnits: number | undefined;
  let previousCTR: number | undefined;
  let previousCPC: number | undefined;
  let previousACoS: number | undefined;
  let previousROAS: number | undefined;
  let previousCR: number | undefined;

  if (previousData && previousData.length > 0) {
    previousImpressions = previousData.reduce((sum, d) => sum + (d.impressions || 0), 0);
    previousClicks = previousData.reduce((sum, d) => sum + (d.clicks || 0), 0);
    previousSpend = previousData.reduce((sum, d) => sum + (d.spend || 0), 0);
    previousSales = previousData.reduce((sum, d) => sum + (d.sales || 0), 0);
    previousOrders = previousData.reduce((sum, d) => sum + (d.orders || 0), 0);
    previousUnits = previousData.reduce((sum, d) => sum + (d.units || 0), 0);

    previousCTR = previousImpressions > 0 ? (previousClicks / previousImpressions) * 100 : 0;
    previousCPC = previousClicks > 0 ? previousSpend / previousClicks : 0;
    previousACoS = previousSales > 0 ? (previousSpend / previousSales) * 100 : 0;
    previousROAS = previousSpend > 0 ? previousSales / previousSpend : 0;
    previousCR = previousClicks > 0 ? (previousOrders / previousClicks) * 100 : 0;
  }

  return {
    impressions: calculatePPCMetricWithAverage(
      currentImpressions,
      currentDays,
      previousImpressions,
      previousDays
    ),
    clicks: calculatePPCMetricWithAverage(
      currentClicks,
      currentDays,
      previousClicks,
      previousDays
    ),
    spend: calculatePPCMetricWithAverage(
      currentSpend,
      currentDays,
      previousSpend,
      previousDays
    ),
    sales: calculatePPCMetricWithAverage(
      currentSales,
      currentDays,
      previousSales,
      previousDays
    ),
    orders: calculatePPCMetricWithAverage(
      currentOrders,
      currentDays,
      previousOrders,
      previousDays
    ),
    units: calculatePPCMetricWithAverage(
      currentUnits,
      currentDays,
      previousUnits,
      previousDays
    ),
    ctr: calculateMetricValue(currentCTR, previousCTR),
    cpc: calculateMetricValue(currentCPC, previousCPC),
    acos: calculateMetricValue(currentACoS, previousACoS),
    roas: calculateMetricValue(currentROAS, previousROAS),
    conversionRate: calculateMetricValue(currentCR, previousCR)
  };
}